// =============================================================================
// 📝 MIRESCO BIZ — Landing Page Content (Phase 1 — Revenue Focus)
// =============================================================================
// EDIT THIS FILE to change text, prices, images, brand names — no coding needed!
// Just change values inside the quotes and save. The website refreshes automatically.
//
// 💡 Quick reference:
//   - To change TEXT:   edit anything inside "double quotes"
//   - To change IMAGE:  paste any image URL (or use /brand/your-image.png)
//   - To change ICON:   use any name from https://lucide.dev/icons/ (case-sensitive)
//   - To change COLOR:  use any hex value like "#C8102E"
// =============================================================================

// ---------- BRAND IDENTITY ----------
export const BRAND = {
  name: "MIRESCO BIZ",
  tagline: "Make Money Today · Build A Business Tomorrow",
  secondaryTagline: "Connecting Nations · Advancing Industries",
  logo: "/brand/miresco-logo.png",
  colors: {
    red: "#C8102E",       // primary
    silver: "#94959A",    // accent (corporate)
    gold: "#D4AF37",      // secondary accent
    charcoal: "#1A1A1A",  // dark
  },
};

// ---------- HEADER ----------
export const NAV_LINKS = [
  { label: "Opportunity TV", href: "/tv", live: true },
  { label: "Pathways",       href: "#pathways" },
  { label: "Opportunities",  href: "#opportunities" },
  { label: "Vendor",         href: "/vendor" },
  { label: "Membership",     href: "#membership" },
  { label: "Coverage",       href: "#coverage" },
];

// ---------- HERO SECTION ----------
export const HERO = {
  tag: "Malaysia's Entrepreneur Growth Platform",
  headline: "Make Money Today.",
  headlineAccent: "Build A Business Tomorrow.",
  subheadline: "Discover verified income opportunities, business opportunities, influencers, partners and growth pathways through one platform.",
  // Three CTAs in priority order
  ctas: [
    { key: "earn",    label: "Explore Income Opportunities", route: "/opportunities?type=affiliate", primary: true,  testid: "hero-cta-earn"  },
    { key: "start",   label: "Start A Business",            route: "/opportunities?type=kiosk",    primary: false, testid: "hero-cta-start" },
    { key: "grow",    label: "Grow My Business",            route: "/vendor",                       primary: false, testid: "hero-cta-grow", external: true },
  ],
  checks: ["100% Free to start", "5 user types", "16 states covered"],
};

// ---------- ENTREPRENEUR JOURNEY ----------
// 6-stage growth roadmap — the core identity of MIRESCO.
export const JOURNEY = [
  { stage: 1, key: "extra-income",       icon: "Coins",       title: "Extra Income",       desc: "Earn your first ringgit as an affiliate — zero capital." },
  { stage: 2, key: "small-business",     icon: "ShoppingBag", title: "Small Business",     desc: "Become an agent or stockist — your first real customers." },
  { stage: 3, key: "entrepreneur",       icon: "Rocket",      title: "Entrepreneur",       desc: "Launch your own kiosk or distribution territory." },
  { stage: 4, key: "business-owner",     icon: "Building2",   title: "Business Owner",     desc: "Build your team — recruit affiliates, agents, distributors." },
  { stage: 5, key: "business-expansion", icon: "TrendingUp",  title: "Business Expansion", desc: "Scale across states, recruit influencers, attract investors." },
  { stage: 6, key: "business-leader",    icon: "Crown",       title: "Business Leader",    desc: "Mentor others, license your brand, lead the ecosystem." },
];

// ---------- BUSINESS CATEGORIES ----------
export const CATEGORIES = [
  { key: "fnb",        icon: "Utensils",    title: "Food & Beverage" },
  { key: "agri",       icon: "Sprout",      title: "Agriculture & Aquaculture" },
  { key: "beauty",     icon: "Sparkles",    title: "Beauty & Wellness" },
  { key: "retail",     icon: "Store",       title: "Retail & Trading" },
  { key: "home",       icon: "Home",        title: "Home & Property Services" },
  { key: "manuf",      icon: "Factory",     title: "Manufacturing & Industrial" },
  { key: "logistics",  icon: "Truck",       title: "Logistics & Transportation" },
  { key: "digital",    icon: "Laptop",      title: "Digital & Professional Services" },
  { key: "edu",        icon: "GraduationCap", title: "Education & Training" },
  { key: "invest",     icon: "TrendingUp",  title: "Investment & Partnership" },
];

// ---------- THREE PATHWAYS ----------
// Displayed immediately after the hero.
// Each pathway = one clear next step for a specific user intent.
export const PATHWAYS = [
  {
    key: "rm0",
    tag: "RM 0 Capital",
    title: "Start with RM 0",
    desc: "Become an affiliate and start earning commissions without holding stock.",
    examples: ["INOCHI Affiliate", "Nano Shield Affiliate", "Vendor Affiliate Programs"],
    cta: "Start with RM 0",
    icon: "Sparkles",
    route: "/opportunities?type=affiliate",
    color: "#C8102E",
  },
  {
    key: "business",
    tag: "Be Your Own Boss",
    title: "Start a Business",
    desc: "Own a kiosk, become a stockist, distributor or business partner.",
    examples: ["Miresco Chocolate", "Miresco Drinks", "Rollsmith", "Stockist & Distribution"],
    cta: "Start a Business",
    icon: "Briefcase",
    route: "/opportunities?type=kiosk",
    color: "#1A1A1A",
  },
  {
    key: "grow",
    tag: "Scale Faster",
    title: "Grow Your Business",
    desc: "List your products, recruit affiliates, find distributors, attract investors and grow.",
    examples: ["Vendor Program", "Investor Matching", "Partnership Opportunities", "Affiliate Recruitment"],
    cta: "Grow My Business",
    icon: "TrendingUp",
    route: "/vendor",
    color: "#D4AF37",
    external: true,
  },
];

// ---------- ABOUT SECTION ----------
export const ABOUT = {
  tag: "About MIRESCO",
  heading: "Discover business opportunities. Create new income streams.",
  body: "MIRESCO BIZ is a Business Opportunity Marketplace where entrepreneurs make money, start businesses, find partners and investors, and expand their networks — all in one trusted platform.",
  image: "https://images.unsplash.com/photo-1556761175-5973dc0f32e7?w=1200&q=80&auto=format&fit=crop",
  imageAlt: "Diverse Malaysian business team collaborating",
  userTypes: [
    { key: "entrepreneur",   icon: "Briefcase",  label: "Entrepreneurs",           desc: "Start earning with affiliate, agent and territory opportunities." },
    { key: "business_owner", icon: "Building2",  label: "Business Owners",         desc: "Recruit agents, distributors, vendors and partners." },
    { key: "investor",       icon: "TrendingUp", label: "Investors",               desc: "Discover deals and strategic partnerships." },
    { key: "contractor",     icon: "Hammer",     label: "Contractors & Providers", desc: "Receive projects and territory-based contracts." },
    { key: "influencer",     icon: "Megaphone",  label: "Influencers & Creators",  desc: "Get paid brand campaigns and collaborations." },
  ],
};

// ---------- FEATURED OPPORTUNITIES (6 PRIORITY BRANDS) ----------
// To add a new brand: copy a block and edit. To remove: delete the block.
export const BRANDS = [
  {
    key: "inochi",
    name: "INOCHI",
    tagline: "The Power of Life · Japanese Anti-Aging Beauty",
    desc: "Premium Japanese skincare with nano-encapsulation technology. In partnership with Picaso Natural Cosmetic Japan since 1935. Recruiting affiliates & agents nationwide.",
    image: "/brand/inochi.png",
    fallback: "linear-gradient(135deg, #7f1d1d 0%, #450a0a 100%)",
    initial: "I",
    showcaseUrl: "/showcase/inochi",
    opportunityTypes: ["Affiliate", "Agent"],
    badge: "Hot",
  },
  {
    key: "nano-shield",
    name: "Nano Shield",
    tagline: "Nano-Tech Surface Protection",
    desc: "Affiliate, vendor and distributor opportunities in advanced surface protection — online & offline.",
    image: "https://images.unsplash.com/photo-1518770660439-4636190af475?w=800&q=80&auto=format&fit=crop",
    fallback: "linear-gradient(135deg, #4338ca 0%, #1e1b4b 100%)",
    initial: "N",
    opportunityTypes: ["Affiliate", "Vendor", "Distributor"],
  },
  {
    key: "vendor-program",
    name: "Vendor Program",
    tagline: "List & Recruit Your Sales Network",
    desc: "Become a MIRESCO vendor. Recruit affiliates, stockists, distributors, influencers and partners.",
    image: "https://images.unsplash.com/photo-1556761175-5973dc0f32e7?w=800&q=80&auto=format&fit=crop",
    fallback: "linear-gradient(135deg, #C8102E 0%, #1A1A1A 100%)",
    initial: "V",
    showcaseUrl: "/vendor",
    opportunityTypes: ["Vendor"],
    badge: "New",
  },
  {
    key: "miresco-chocolate",
    name: "Miresco Chocolate",
    tagline: "Premium Japanese-Inspired Chocolate Kiosk",
    desc: "Run a branded Miresco Chocolate kiosk featuring premium Japanese-inspired beverages and chocolate. Full kiosk setup, branding and supply chain included.",
    image: "/brand/miresco-chocolate.png",
    fallback: "linear-gradient(135deg, #78350f 0%, #292524 100%)",
    initial: "C",
    opportunityTypes: ["Kiosk", "Stockist"],
  },
  {
    key: "miresco-drinks",
    name: "Miresco Drinks",
    tagline: "Premium Beverage Franchise",
    desc: "Beverage kiosk franchise with proven foot-traffic locations and central supply chain.",
    image: "https://images.unsplash.com/photo-1556881286-fc6915169721?w=800&q=80&auto=format&fit=crop",
    fallback: "linear-gradient(135deg, #0e7490 0%, #164e63 100%)",
    initial: "D",
    opportunityTypes: ["Kiosk", "Distributor"],
  },
  {
    key: "rollsmith",
    name: "Rollsmith",
    tagline: "Asian Rolls & Rice Sheets · Malaysian Delights",
    desc: "Freshly-rolled Popia Basah franchise — Sayur, Ayam, Daging, Crab Meat and the new Rendang Dendeng. Halal-certified. Multiple territory openings.",
    image: "/brand/rollsmith.png",
    fallback: "linear-gradient(135deg, #b45309 0%, #451a03 100%)",
    initial: "R",
    opportunityTypes: ["Kiosk", "Stockist"],
  },
  {
    key: "soupway",
    name: "SOUPWAY",
    tagline: "Premium Asian Soup Concept",
    desc: "Quick-service soup kiosk concept. Multiple territory and stockist openings nationwide.",
    image: "https://images.unsplash.com/photo-1547592180-85f173990554?w=800&q=80&auto=format&fit=crop",
    fallback: "linear-gradient(135deg, #b91c1c 0%, #450a0a 100%)",
    initial: "S",
    opportunityTypes: ["Kiosk", "Stockist", "Distributor"],
    badge: "New",
  },
  {
    key: "car-bootpreneur",
    name: "Car Bootpreneur",
    tagline: "Mobile Business · Sell From Your Car Boot",
    desc: "Turn your car into a business. Low-capital mobile retail program with full training and supply chain.",
    image: "https://images.unsplash.com/photo-1492144534655-ae79c964c9d7?w=800&q=80&auto=format&fit=crop",
    fallback: "linear-gradient(135deg, #1f2937 0%, #030712 100%)",
    initial: "B",
    opportunityTypes: ["Affiliate", "Agent"],
    badge: "Hot",
  },
];

// ---------- PROGRAMS ----------
export const PROGRAMS = [
  { key: "territory",   icon: "MapPin",    title: "Territory Program",   desc: "Claim exclusive coverage of Malaysian states, districts and cities." },
  { key: "kiosk",       icon: "Store",     title: "Kiosk Program",       desc: "Run a branded kiosk in high-traffic shopping locations." },
  { key: "vendor",      icon: "Box",       title: "Vendor Program",      desc: "Sell trusted products under an established brand." },
  { key: "stockist",    icon: "Layers",    title: "Stockist Program",    desc: "Hold and supply inventory across regional markets." },
  { key: "distributor", icon: "Truck",     title: "Distributor Program", desc: "Manage downstream distribution across multiple states." },
  { key: "listing",     icon: "Building2", title: "Business Listing",    desc: "List your business and get discovered by partners and investors." },
];

// ---------- MEMBERSHIP TIERS ----------
export const TIERS = [
  {
    key: "free",
    name: "Free Member",
    price: "RM 0",
    per: "/forever",
    accent: "#1A1A1A",
    icon: "Shield",
    popular: false,
    features: ["Community Access", "Business Updates", "Browse Opportunities", "Apply to Selected Programs"],
  },
  {
    key: "pro",
    name: "Pro Member",
    price: "RM 199",
    per: "/month",
    accent: "#C8102E",
    icon: "Star",
    popular: true,
    features: ["Everything in Free", "Enhanced Visibility", "Training & Courses", "Priority Applications", "Funding Guidance"],
  },
  {
    key: "elite",
    name: "Elite Member",
    price: "RM 799",
    per: "/month",
    accent: "#D4AF37",
    icon: "Crown",
    popular: false,
    features: ["Everything in Pro", "Vendor Tools", "Territory Management", "Priority Listings", "Strategic Networking", "Dedicated Account Manager"],
  },
];

// ---------- COVERAGE (MALAYSIA) ----------
export const COVERAGE = {
  tag: "Coverage",
  heading: "Available in all 16 states across Malaysia.",
  body: "From Peninsular Malaysia to Sabah and Sarawak — MIRESCO BIZ covers every state and federal territory, with structured territory programs and verified members.",
  image: "https://images.unsplash.com/photo-1596422846543-75c6fc197f07?w=1200&q=80&auto=format&fit=crop",
  imageAlt: "Kuala Lumpur skyline with Petronas Towers",
  keyStates: ["Selangor", "Kuala Lumpur", "Penang", "Johor", "Sabah", "Sarawak"],
  allStates: [
    "Johor", "Kedah", "Kelantan", "Kuala Lumpur", "Labuan", "Melaka",
    "Negeri Sembilan", "Pahang", "Penang", "Perak", "Perlis", "Putrajaya",
    "Sabah", "Sarawak", "Selangor", "Terengganu",
  ],
};

// ---------- COMING SOON (FUTURE FEATURES) ----------
// These are placeholders. To activate one, ask the dev team — do NOT remove the "comingSoon" flag yourself.
export const COMING_SOON = [
  { key: "live-commerce",   icon: "Video",     title: "MIRESCO Live Commerce", desc: "In-app live shopping events with vendors & influencers." },
  { key: "live-streaming",  icon: "Radio",     title: "Live Streaming",        desc: "Vendor & affiliate live broadcast tools." },
  { key: "video-selling",   icon: "Film",      title: "Video Selling",         desc: "Short-form video catalog with one-tap apply." },
  { key: "ai-matching",     icon: "Sparkles",  title: "AI Matching",           desc: "AI matches users to ideal opportunities & territories." },
  { key: "indonesia",       icon: "Globe",     title: "Indonesia Expansion",   desc: "Cross-border opportunities in Indonesia." },
  { key: "japan",           icon: "Globe",     title: "Japan Expansion",       desc: "Premium brand partnerships from Japan." },
];

// ---------- FINAL CTA ----------
export const FINAL_CTA = {
  heading: "Ready to grow with MIRESCO?",
  body: "Join Malaysia's business opportunity marketplace. Discover affiliate, vendor, kiosk and territory programs — start in minutes.",
  webPreviewUrl: "https://www.miresco.biz",
  webPreviewLabel: "Launch Web App",
};

// ---------- APP STORE BADGES ----------
export const APP_STORES = {
  apple: { comingSoon: true,  url: "#" },
  google:{ comingSoon: true,  url: "#" },
};

// ---------- FOOTER ----------
export const FOOTER = {
  blurb: "Malaysia's business opportunity marketplace — connecting nations, advancing industries, building the next generation of businesses.",
  email: "support@miresco.biz",
  socials: [
    { key: "instagram", icon: "Instagram", url: "#", label: "Instagram" },
    { key: "facebook",  icon: "Facebook",  url: "#", label: "Facebook" },
    { key: "tiktok",    icon: "Music2",    url: "#", label: "TikTok"   },
  ],
  copyrightOwner: "MIRESCO BIZ",
};
